import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  FRONT_WARNING,
  LEAD_CARD_PATH,
  SALES_FUNNELS_LEAD_CARD_PATH,
} from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { BaseState } from './useBaseState';

type UseLeadCardNavigationParams = Pick<BaseState, 'selectedLeadId'>;

interface UseLeadCardNavigationResult {
  openLeadCardPage(): void;
}

export const useNavigation = ({
  selectedLeadId,
}: UseLeadCardNavigationParams): UseLeadCardNavigationResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const openLeadCardPage = useCallback(() => {
    if (selectedLeadId === null) {
      showNotification('Лид не был распознан', FRONT_WARNING);
      return;
    }

    const currentSearchParams = new URLSearchParams(location.search);
    const isFunnelSource =
      currentSearchParams.get('source') === 'funnels' ||
      location.pathname === SALES_FUNNELS_LEAD_CARD_PATH;

    currentSearchParams.set('leadId', String(selectedLeadId));

    if (isFunnelSource) {
      currentSearchParams.set('source', 'funnels');
      navigate(
        `${SALES_FUNNELS_LEAD_CARD_PATH}?${currentSearchParams.toString()}`
      );
      return;
    }

    currentSearchParams.set('source', 'registry');
    navigate(`${LEAD_CARD_PATH}?${currentSearchParams.toString()}`);
  }, [
    location.pathname,
    location.search,
    navigate,
    selectedLeadId,
    showNotification,
  ]);

  return useMemo(
    () => ({
      openLeadCardPage,
    }),
    [openLeadCardPage]
  );
};
